import React, { useState, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiEye, FiEyeOff } from 'react-icons/fi';

const RegisterContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  background-color: white;
`;

const Logo = styled(motion.div)`
  width: 80px;
  height: 80px;
  background-color: #ff0000;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-family: 'Poppins', sans-serif;
  font-weight: bold;
  font-size: 16px;
  text-align: center;
  line-height: 1.2;
  margin-bottom: 30px;

  span {
    font-weight: 900;
  }
`;

const Title = styled(motion.h1)`
  font-size: 28px;
  margin-bottom: 10px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  color: #333;
`;

const Subtitle = styled(motion.p)`
  font-size: 16px;
  color: #666;
  margin-bottom: 40px;
  font-family: 'Poppins', sans-serif;
  text-align: center;
`;

const Form = styled.form`
  width: 100%;
  max-width: 320px;
`;

const InputContainer = styled.div`
  margin-bottom: 20px;
  position: relative;
`;

const Input = styled(motion.input)`
  width: 100%;
  padding: 15px;
  border: 2px solid #eee;
  border-radius: 12px;
  font-size: 16px;
  font-family: 'Poppins', sans-serif;
  outline: none;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: #ff0000;
    box-shadow: 0 0 0 2px rgba(255, 0, 0, 0.1);
  }
`;

const PasswordInput = styled(Input)`
  padding-right: 45px;
`;

const EyeIcon = styled(motion.button)`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  font-size: 20px;
  
  &:hover {
    color: #ff0000;
  }
`;

const ValidationMessage = styled(motion.p)`
  color: #ff0000;
  font-size: 12px;
  margin-top: 5px;
  font-family: 'Poppins', sans-serif;
`;

const RegisterButton = styled(motion.button)`
  width: 100%;
  padding: 15px;
  background-color: #ff0000;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 20px;
  font-family: 'Poppins', sans-serif;
  
  &:disabled {
    background-color: #ffcccc;
    cursor: not-allowed;
  }
`;

const LoginLink = styled(motion.p)`
  margin-top: 20px;
  font-size: 14px;
  color: #666;
  text-align: center;
  font-family: 'Poppins', sans-serif;
  
  a {
    color: #ff0000;
    text-decoration: none;
    font-weight: 600;
    margin-left: 5px;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/login');
  };

  const isValidPassword = formData.password.length >= 8 && 
    /[a-zA-Z]/.test(formData.password) && 
    /\d/.test(formData.password);

  const isValidForm = 
    formData.username &&
    formData.email &&
    isValidPassword &&
    formData.password === formData.confirmPassword;

  return (
    <RegisterContainer
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <Logo
        variants={itemVariants}
        whileHover={{ scale: 1.1, rotate: 360 }}
        transition={{ duration: 0.5 }}
      >
        STYLE<span>BOX</span>
      </Logo>
      
      <Title variants={itemVariants}>Create Account</Title>
      <Subtitle variants={itemVariants}>Please fill in your details to register</Subtitle>
      
      <Form onSubmit={handleSubmit}>
        <InputContainer>
          <Input
            type="text"
            name="username"
            placeholder="Username"
            value={formData.username}
            onChange={handleChange}
            variants={itemVariants}
            whileFocus={{ scale: 1.01 }}
          />
        </InputContainer>

        <InputContainer>
          <Input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            variants={itemVariants}
            whileFocus={{ scale: 1.01 }}
          />
        </InputContainer>
        
        <InputContainer>
          <PasswordInput
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            variants={itemVariants}
            whileFocus={{ scale: 1.01 }}
          />
          <EyeIcon
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {showPassword ? <FiEyeOff /> : <FiEye />}
          </EyeIcon>
        </InputContainer>

        <InputContainer>
          <PasswordInput
            type={showPassword ? "text" : "password"}
            name="confirmPassword"
            placeholder="Confirm Password"
            value={formData.confirmPassword}
            onChange={handleChange}
            variants={itemVariants}
            whileFocus={{ scale: 1.01 }}
          />
          <EyeIcon
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {showPassword ? <FiEyeOff /> : <FiEye />}
          </EyeIcon>
        </InputContainer>
        
        {formData.password && !isValidPassword && (
          <ValidationMessage
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            At least 8 characters, containing a letter and a number
          </ValidationMessage>
        )}
        
        {formData.confirmPassword && formData.password !== formData.confirmPassword && (
          <ValidationMessage
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Passwords do not match
          </ValidationMessage>
        )}
        
        <RegisterButton
          type="submit"
          disabled={!isValidForm}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          variants={itemVariants}
        >
          Register
        </RegisterButton>

        <LoginLink variants={itemVariants}>
          Already have an account?
          <motion.a
            href="/login"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Login here
          </motion.a>
        </LoginLink>
      </Form>
    </RegisterContainer>
  );
};

export default Register; 